import * as React from "react";

import {cs}             from "@Common/react/chain-services";
import {FComponent}     from "@Common/react/f-component";
import {CcApis3}        from "@Common/tools/cc-integration/cc_apis/cc-apis-3";
import {Types}          from "@Common/types";
import {loadScript}     from "@Common/ui-components/browser/load-script";
import {isDevXd}        from "@Common/ui-components/debug/is-dev";
import {getNestedValue,
        setNestedValue} from "@Common/utils/arr-path";
import {isNotEmpty,
        mapValues,
        omit}           from "@Common/utils/objects";

import {addImageReferenceToProductTags} from "../add-image-reference-to-product-tags";
import {getBindingIndexFromBindingData} from "../common/get-binding-index-from-binding-data";
import {appendUpdateFieldEvent}         from "../tools/format-text";
import * as variableDataHandler         from "../tools/variable-data-handler";

import {addEditorSelectImageButtonHandlers}                    from "./add-editor-select-image-button-handlers";
import {addImageReferencesToProductTagsFromInitializationData} from "./add-image-references-to-product-tags-from-initialization-data";
import {setInputData}                                          from "./input-data";
import {mapBindingData}                                        from "./map-binding-data";
import {mapBindingDropdown}                                    from "./map-binding-dropdowns";
import {getBoundData,
        setDefaultStyle}                                       from "./map_style_binding";
import {setOverrideData}                                       from "./override-data";
import {searchIframeObjectModel}                               from "./search-iframe-object-model";
import * as IFrameAPIEvents                                    from "./subscribe";


interface S {
    currentSurfaceIndex     : any,
    fullScreen              : boolean,
    initialized             : boolean,
    productModel            : any,
    initializeError         : any,
    pickpackStyleSelections : any,
    surfaceIdMap            : any,
}
interface P {
    apis                    : Types.Apis.Loaders.XpressoLoadApis,
    ccApis                  : Types.Apis.Loaders.CustomersCanvasApis,
    ccHostResponse          : any,
    ccConfig                : any,
    systemConstants         : Types.Apis.Models.SystemConstants,
    editing                 : any,
    inputData               : any,
    itemId                  : any,
    componentId             : any,
    useAdvancedEditingMode  : any,
    pickpackOptions         : any,
    pickpackStyles          : any,
    templateVariations      : any,
    distributionType        : string,
    qrCodeLineItemValue     : string,
    qrCodeUrlValue          : string,
    onNotifyFailover        : (internetIsConnected:boolean, failoverServerIsAvailable:boolean, changesSaved:boolean) => void,
    onNotifyReset           : () => void,
    logFailoverIncident     : any,
    next                    : any,
}

export class CcEditor extends FComponent<P,S>  {

    bindingData;
    ccVersion;
    ccVersionMajor;
    editorDom;
    editor;
    inputData;
    oldInputData;
    override;
    product;
    stateId;
    allowsVariableData;
    pickpackOptions;

    constructor(props, context) {
        super(props, context);

        if (isDevXd()) console.log("CcEditor Props", this.props);

        // refresh the page if provided host doesn't match the host on window["CustomersCanvas"] - this is a temporary
        // solution to an issue where the CC Editor stays pointed to a previous CC server when loading the editor in
        // APM, using the Cancel button, then selecting another product - there should be a way to flush CC resources
        // from the browser, but just doing a full page refresh for now
        if (window["CustomersCanvas"]?.IframeApi.editorUrl &&
            this.props.ccHostResponse.value?.ccHost &&
            this.props.ccHostResponse.value.ccHost !== window["CustomersCanvas"].IframeApi.editorUrl) {
            if (isDevXd()) {
                console.log("this.props.ccHostResponse.value.ccHost", this.props.ccHostResponse.value.ccHost);
                console.log("window[\"CustomersCanvas\"].IframeApi.editorUrl", window["CustomersCanvas"].IframeApi.editorUrl);
                alert("Reloading page because provided host doesn't match");
            }
            location.reload();
        }

        this.state = {
            initializeError: null,
            currentSurfaceIndex: null,
            fullScreen: false,
            initialized: false,
            productModel: null,
            pickpackStyleSelections: null,
            surfaceIdMap: null,
        };

    };

    // Calls a method against the CC editor. Any CC iframe API calls should be made through this intermediary so that
    // failures can be intercepted for failover handling.
    callEditorMethod = async (name, ...args) => {

        // run the iframe API method - return if no exception is thrown
        try {
            return await this.editor[name](...args);
        }
        // FAILOVER ACTIONS
        catch (err) {

            if (err.exception?.ajaxResponse && err.exception.ajaxResponse.status !== 200) {

                const currentEditorHost = this.editor._context.editorUrl;
                const currentEditorVersion = await this.editor.eval("spEditor.productHandler.product.version");

                // check for next available host
                const ccApi = CcApis3({host: this.props.systemConstants.DS_SERVER_URL});
                const failoverHostData = await ccApi.getHost({
                    stateId: this.stateId
                }).catch(() => null); // return null if can't reach host service

                // if no host found, let the user know there was an error
                if (!failoverHostData?.ccHost) {
                    this.props.logFailoverIncident({retryHost: "", exception: err});
                    this.props.onNotifyFailover(failoverHostData !== null, false, false);
                    return null;
                }

                // log the incident
                this.props.logFailoverIncident({retryHost: failoverHostData.ccHost, exception: err});

                // if same host was found, then momentary issue - retry the original call and continue as normal
                if (failoverHostData.ccHost === currentEditorHost) return this.callEditorMethod(name, ...args);

                /*
                // DISABLING SAVE AGAINST FAILOVER - THIS CAUSES AN ERROR IF IMAGES WERE ADDED SINCE THE LAST SAVE -
                // BEFORE RE-ENABLING, CHECK TO SEE IF IMAGE REFERENCES IN THE EDITOR CAN BE MODIFIED TO POINT TO THE
                // FAILOVER SERVER

                // Additional info:
                //   If adding an image a URL like this will load the image:
                //   https://ds-dev02.xpressdocs.com/api/ccviewer/img?f=b32e9921-4525-4695-8375-5cb4c5abb1e4&w=416&h=278
                //   The same URL but against ds-dev03 will not load. After saving, the image is still not available
                //   on ds-dev03. After refreshing the page and reloading the editor, then the ds-dev03 path works. This
                //   may mean that images are only stored in a cached version of the state, local to the DS server (not
                //   in S3), and saving changes against the failover may not be feasible.

                // if a new host was returned adjust the editor host to the failover host
                await this.editor.eval("spEditor.configuration.appDomainUrl = '" + failoverHostData.ccHost + "'")
                this.editor._context.editorUrl = failoverHostData.ccHost;
                if (this.props.ccHostResponse.value?.ccHost) this.props.ccHostResponse.value.ccHost = failoverHostData.ccHost; // when re-enabling, update to use ccHostResponse.onChange

                // save the state
                const saveResponse = await this.saveProductState(this.stateId);
                const saveWasSuccessful = saveResponse?.stateId === this.stateId;
                */
                const saveWasSuccessful = false; // TODO: Remove this after uncommenting the above

                this.props.onNotifyFailover(true, true, saveWasSuccessful);

            }
            else if( err.msg ) {
                return err.msg;
            }

            return null;

        }
    };

    saveProductState = async ({ccConfig}) => {
        if (isDevXd()) console.log("CcEditor saving product state", this.stateId);

        // save image asset references
        await this.saveProductImageAssetReferences({ccConfig});

        // generate the state ID if doesn't already exist
        if (this.props.itemId && !this.stateId) {
            let randomStr = Math.random().toString(36);
            this.stateId = `${randomStr.substring(2,7)}-${this.props.itemId}-${randomStr.substring(7)}`;
        }

        // save the host and version to the listings DB - add any other helpful attributes here if needed
        if (this.props.ccHostResponse.value) {
            await this.props.ccApis.saveStateAttributes(this.stateId, {
                ccHost: this.props.ccHostResponse.value.ccHost.replace("https://", ""),
                ccVersion: this.ccVersion
            });
        }

        // save the state against CC
        return await this.callEditorMethod("saveProduct", this.stateId);

    };

    saveProductImageAssetReferences = async ({ccConfig}) => {

        // skip save if no order ID is available
        if (!this.props.itemId) {
            console.log("CcEditor skipping image reference save because no order ID is available", this.props);
            return;
        }

        const product = await this.callEditorMethod("getProduct");
        let tags = await product.getTags();
        let productModel = await product.getProductModel();
        let items = await productModel.getAllItems();

        // find all user-editable image references in the product
        let imageReferences = [];
        items.forEach(item => {

            // skip items that do not have valid image assets
            if (item.type !== "PlaceholderItem" || // check image areas only
                !item._content || // use only areas with content
                !item._name || // use only those with a name - some duplicate records exist without a name
                !item._visible || // use only visible items
                item._locked // use only user-editable items
            ) {
                return;
            }

            // find required attributes of the image area
            const surfaceName = item._parentContainer.parentComponent._name;
            const variableName = item._name;

            // find the image index from template binding data if available - binding index will be added to the image
            // label, which will aide in determining similar image areas when copying images between templates
            const bindingIndex = getBindingIndexFromBindingData(ccConfig.templateBindingData?.[surfaceName]?.[variableName]);

            // find the image reference from product tags, where surface and variable match the image area
            const tagData = tags?.imageSources?.find(el => el.surfaceName === surfaceName && el.variableName === variableName);

            // add reference to a known image if tag data found
            if (tagData?.assetId) {
                imageReferences.push({
                    assetType: tagData.assetType,
                    orderImageLabel: surfaceName + "|" + variableName + (bindingIndex ? ("|binding"+bindingIndex) : ""),
                    assetId: tagData.assetId,
                    assetUrl: ""
                });
            } else {
                // if no tag data found, reference a URL so that the image can be downloaded to the order
                const imageSource = item._content._source;
                imageReferences.push({
                    assetType: "order",
                    orderImageLabel: surfaceName + "|" + variableName + (bindingIndex ? ("|binding"+bindingIndex) : ""),
                    assetId: "",
                    assetUrl: `${this.props.ccHostResponse.value?.ccHost}/api/ccviewer/pth?f=${imageSource._storageId}`
                });
            }

        });

        if (isDevXd()) console.log("CcEditor saving image references", imageReferences);

        // save image references
        const savedImageReferences = await this.props.apis.next.order.saveImageAssetReferences(this.props.itemId, imageReferences, this.props.componentId);

        // write product tags for any newly-created asset IDs
        for (const savedImageReference of savedImageReferences) { // not using foreach because await is not honored and each tag addition must be done sequentially

            const [surfaceName, variableName] = savedImageReference.orderImageLabel.split("|");
            if (!surfaceName || !variableName) return;

            // update the asset ID if exists and changed
            let foundExistingTag = false;

            // noinspection ES6MissingAwait
            tags?.imageSources.forEach(async (imageSource) => {
                if (imageSource.surfaceName === surfaceName && imageSource.variableName === variableName) {
                    foundExistingTag = true;
                    if (imageSource.assetId != savedImageReference.assetId) {
                        await addImageReferenceToProductTags (product, imageSource.assetType, savedImageReference.assetId, surfaceName, variableName);
                    }
                }
            });

            // insert the new tag if doesn't already exist
            if (!foundExistingTag) {
                await addImageReferenceToProductTags (product, "order", savedImageReference.assetId, surfaceName, variableName);
            }

        }

    };

    initialize = async ({userData, ccConfig, assetSources}) => {
        // let stateBroken = false;
        try{
            let stateId = userData.ccStateId;
            let userCreatedStateId = userData.userCreatedStateId;
            let ccSourceStateId = userData.ccSourceStateId;
            isDevXd() ? console.log("stateId", userData.ccStateId): null;
            const {sourceData, overrideData} = userData;
            this.override = overrideData? overrideData : {};
            this.allowsVariableData = !!userData.sourceData?.xd?.templateOptions?.allowVariableData && this.props.distributionType === "mailing";
            let stateIsValid = stateId ? await this.props.ccApis.authenticateState(stateId) : false;
            //reset pickpackOptions text data in case variations changed while leaving settings from Product Options like style selection alone.
            this.pickpackOptions = setNestedValue(this.props.pickpackOptions, ['text'], {});
            // ccConfig.editorConfigurations.assetSources = config.editorConfigurations.assetSources;
            // this.bindingData = ccConfig.templateBindingData;

            if( stateIsValid?.LastModified && ccConfig.forceResetDate ) {
                let dateModified = new Date( stateIsValid.LastModified );
                let forceResetDate = new Date( ccConfig.forceResetDate );

                if( dateModified < forceResetDate ) {
                    if(this.props.itemId){
                        let item = await this.props.apis.next.item.getItem(this.props.itemId);
                        if(item?.metaData?.ccReference){
                            item.metaData.oldCcReference = item.metaData.ccReference;
                            delete item.metaData.ccReference;
                            await this.props.apis.next.item.updateItem(item);
                        }
                    }

                    stateIsValid = null;
                    stateId = null;
                    this.props.onNotifyReset();
                }
            }

            if (isDevXd()) {
                console.log("CcEditor.initialize config", ccConfig);
                console.log("CcEditor.initialize assetSources", assetSources);
                console.log("CcEditor.initialize templateBindingData", ccConfig.templateBindingData);
            }
            let pickpackStyleSelections = {};
            if( this.props.templateVariations && !this.props.templateVariations?.error && this.props.templateVariations?.fields ) {
                for( const field of this.props.templateVariations.fields ) {
                    if( field?.type == 'options' && field?.proofing?.optionLinks ) {
                        const uiName = this.props.pickpackStyles.find((option) => option.vendorLabel === field.name)?.uiValue;
                        const optionId = field.options.find((option) => option.label === uiName)?.id;
                        const optionName = field.proofing.optionLinks?.[optionId];
                        if( optionName && ccConfig.styleIdMap ) {
                            Object.entries(ccConfig.styleIdMap).forEach(([surface, obj]) => {
                                Object.entries(obj).forEach(([styleId, styleName]) => {
                                    if( ccConfig.styleDefaults?.[surface]?.[styleName] && styleId == field.proofing.styleId ) {
                                        if( !pickpackStyleSelections[surface] ) {
                                            pickpackStyleSelections[surface] = {};
                                        }
                                        pickpackStyleSelections[surface][styleName] = optionName;
                                    }
                                });
                            });
                        }
                    }
                }
            }
            if (stateIsValid){
                isDevXd() ? console.log("TIME_START ", Math.floor(Date.now() / 1000)): null;
                this.stateId = stateId;

                try {
                    await this.loadCanvas({
                        ccConfig, editorConfigurations: ccConfig.editorConfigurations, assetSources, stateId,
                        userData,
                    });
                    let productModel = await this.product.getProductModel();
                    let items = await productModel.getAllItems();
                    items = await getBoundData( items, ccConfig.styleBindingData );

                    if( Object.keys(pickpackStyleSelections).length ) {
                        //getting changed surfaces for V7 with textWrap issue
                        const [productThemes, changedSurfaces] = [
                            ...await setDefaultStyle(
                                pickpackStyleSelections,
                                ccConfig.styleConfigurations,
                                items,
                                this.ccVersion,
                                this.product.surfaces[this.product.currentSurfaceIndex].name
                            )
                        ];

                        //communicating with the canvas
                        for(let x=0; x<3; x++) {
                            try{
                                await this.product.setProductModel(productModel);
                                break;
                            } catch(e){
                                isDevXd() ? console.log("Error setting the product model ", e): null;
                            }
                        }

                        await Promise.all( productThemes.map(async (optionName, key)=>{
                            for(let x=0; x<3; x++) {
                                try{
                                    let response = false;
                                    //version 7 text wrap isn't updating without applyProductTheme or setItem while the items surface is loaded.
                                    if( this.ccVersionMajor >= 7 && key == productThemes.length - 1 && changedSurfaces.length ) {
                                        let currentSurfaceIndex = this.product.currentSurfaceIndex;
                                        for( const [index, surface] of this.product.surfaces.entries() ) {
                                            if( changedSurfaces.includes(surface?.name) ) {
                                                await this.product.switchTo(this.product.surfaces[index]);
                                                response = await this.callEditorMethod("applyProductTheme", optionName);
                                            }
                                        }
                                        await this.product.switchTo(this.product.surfaces[currentSurfaceIndex]);
                                    }
                                    else {
                                        response = await this.callEditorMethod("applyProductTheme", optionName);
                                    }
                                    isDevXd() ? console.log(`applyProductTheme attempt ${x}`, response) : null;
                                    if( response === true ) break;
                                }catch(e){
                                    isDevXd() ? console.log('applyProductTheme failed for ', optionName, e): null;
                                }
                            }
                        }));
                    }
                    else {
                        //communicating with the canvas
                        for(let x=0; x<3; x++) {
                            try{
                                await this.product.setProductModel(productModel);
                                break;
                            } catch(e){
                                isDevXd() ? console.log("Error setting the product model ", e): null;
                            }
                        }
                    }

                    if( userData?.sourceData?.xd?.templateOptions?.options?.find(x => x.devKey === "use_company_color_set")?.value && isNotEmpty(ccConfig.colorSet??{}) ) {
                        let productTheme = Object.keys(ccConfig.colorSet)[0];
                        for(let x=0; x<3; x++) {
                            try {
                                let response = await this.callEditorMethod("applyProductTheme", productTheme);
                                isDevXd() ? console.log(`applyProductTheme attempt ${x}`, response) : null;
                                if( response === true ) break;
                            }
                            catch(e) {
                                isDevXd() ? console.log('applyProductTheme failed for ', productTheme, e) : null;
                            }
                        }
                    }

                    if( this.props.inputData && Object.keys(this.props.inputData).length >= 0 ){
                        this.inputData = this.props.inputData;
                    } else {
                        this.inputData = await setInputData(this.product, {});
                    }
                    if( Object.keys(pickpackStyleSelections).length ) {
                        Object.entries(pickpackStyleSelections).forEach(([currentSide, options]) => {
                            Object.entries(options).forEach(([styleName, optionName]) => {
                                this.inputData = setNestedValue(this.inputData, ['styles', currentSide, styleName], optionName);
                            })
                        });
                        await this.callEditorMethod("saveProduct", this.stateId);
                    }
                }
                catch (e) {
                    isDevXd() ? console.log("Error with stateId/mapBindingData: " , e): null;
                    // throw "Error with stateId/mapBindingData";
                }
            }
            else {
                isDevXd() ? console.log("TIME_START ", Math.floor(Date.now() / 1000)): null;

                // load merge fields if available
                const mergeFields = this.allowsVariableData && await this.props.apis.next.templateAdmin.getVariableDataMergeFields();

                // apply bindings
                let userInfo = await mapBindingData(
                    sourceData, ccConfig.templateBindingData, ccConfig.templateConfigurations,
                    this.props.ccApis, overrideData, ccConfig.customFieldsData, ccConfig.ccJson,
                    this.props.ccHostResponse.value?.ccHost, this.props.itemId,
                    mergeFields, this.props.qrCodeLineItemValue, this.props.qrCodeUrlValue
                );

                isDevXd() ? console.log("config",ccConfig): null;

                if (userCreatedStateId) {
                    const cc_authentication = await this.props.ccApis.getToken();
                    const userId = cc_authentication.userGUID;
                    const tokenId = cc_authentication.tokenId;

                    isDevXd() ? console.log("userCreatedStateId", userCreatedStateId): null;
                    await this.loadCanvas({
                        ccConfig,
                        editorConfigurations: {
                            ...ccConfig.editorConfigurations,
                            userId,
                            tokenId,
                            autoLoadUserInfo: true,
                        },
                        userInfo: omit(userInfo, ["qrValues"]),
                        assetSources,
                        userData,
                        stateId: userCreatedStateId,
                    });
                    let productModel = await this.product.getProductModel();
                    let items = await productModel.getAllItems();
                    //gathering field data
                    items = await getBoundData( items, ccConfig.styleBindingData );

                    let data = await mapBindingDropdown(
                        ccConfig.templateBindingData, // not mutated
                        this.override, // not mutated
                        sourceData,
                        userInfo,
                        items,
                        this.product,
                        ccConfig.templateConfigurations,
                        ccConfig.customFieldsData
                    );
                    if(Object.keys(data).length !== 0){
                        isDevXd() ? console.log("loadUserInfo"): null;
                        await this.callEditorMethod("loadUserInfo", data);
                    } else {
                        isDevXd() ? console.log('nothing to bind'): null;

                    }
                    this.stateId = userCreatedStateId.replace('editing', 'preview');
                    await this.callEditorMethod("saveProduct", this.stateId);
                }
                else if( ccSourceStateId ) {
                    let result = await this.props.ccApis.cloneState({
                        templateIds: userData.templateIds, orderId: this.props.itemId, ccStateId: ccSourceStateId, sourceUserId: `company-${sourceData.xd.company.idCompany}`
                    });

                    isDevXd() ? console.log('Clone user created state for user order', result): null;

                    this.stateId = result.ccReference;

                    await this.loadCanvas({
                        ccConfig,
                        editorConfigurations: {
                            ...ccConfig.editorConfigurations,
                            autoLoadUserInfo: true,
                        },
                        userInfo: omit(userInfo, ["qrValues"]),
                        assetSources,
                        userData,
                        stateId: this.stateId,
                    });
                    let productModel = await this.product.getProductModel();
                    let items = await productModel.getAllItems();
                    //gathering field data
                    items = await getBoundData( items, ccConfig.styleBindingData );

                    let data = await mapBindingDropdown(
                        ccConfig.templateBindingData, // not mutated
                        this.override, // not mutated
                        sourceData,
                        userInfo,
                        items,
                        this.product,
                        ccConfig.templateConfigurations,
                        ccConfig.customFieldsData
                    );
                    if(Object.keys(data).length !== 0){
                        isDevXd() ? console.log("loadUserInfo"): null;
                        await this.callEditorMethod("loadUserInfo", data);
                    } else {
                        isDevXd() ? console.log('nothing to bind'): null;
                    }
                    await this.callEditorMethod("saveProduct", this.stateId);
                }
                else {
                    await this.loadCanvas({
                        ccConfig,
                        editorConfigurations: {
                            ...ccConfig.editorConfigurations,
                            autoLoadUserInfo: true,
                        },
                        userInfo: omit(userInfo, ["qrValues"]),
                        assetSources,
                        userData,
                    });
                    await this.setCustomFields( ccConfig.customFieldsData, userInfo, this.product);
                    let productModel = await this.product.getProductModel();
                    let items = await productModel.getAllItems();
                    //gathering field data
                    items = await getBoundData( items, ccConfig.styleBindingData );

                    const styleDefaults = mapValues(ccConfig.styleDefaults, (v, surface) => {
                        const userValues = userData.overrideData?.styleDefaults?.[surface];
                        if(typeof userValues !== 'object' || userValues == null){
                            return {
                                ...v,
                                ...pickpackStyleSelections?.[surface]
                            };
                        }
                        return ({
                            ...v,
                            ...userValues,
                            ...pickpackStyleSelections?.[surface]
                        });
                    });
                    //getting changed surfaces for V7 with textWrap issue
                    let [productThemes, changedSurfaces] = [
                        ...await setDefaultStyle(
                            styleDefaults,
                            ccConfig.styleConfigurations,
                            items,
                            this.ccVersion,
                            this.product.surfaces[this.product.currentSurfaceIndex].name
                        )
                    ];
                    productThemes = [...productThemes, ...(isNotEmpty(ccConfig.colorSet??{}) ? [
                        Object.keys(ccConfig.colorSet)[0]
                    ] : [])];

                    let data = await mapBindingDropdown(
                        ccConfig.templateBindingData, // not mutated
                        this.override, // not mutated
                        sourceData,
                        userInfo,
                        items,
                        this.product,
                        ccConfig.templateConfigurations,
                        ccConfig.customFieldsData
                    );

                    //communicating with the canvas
                    for(let x=0; x<3; x++) {
                        try{
                            await this.product.setProductModel(productModel);
                            break;
                        } catch(e){
                            isDevXd() ? console.log("Error setting the product model ", e): null;
                        }
                    }

                    await Promise.all( productThemes.map(async (optionName, key)=>{
                        for(let x=0; x<3; x++) {
                            try{
                                let response = false;
                                //version 7 text wrap isn't updating without applyProductTheme or setItem while the items surface is loaded.
                                if( this.ccVersionMajor >= 7 && key == productThemes.length - 1 && changedSurfaces.length ) {
                                    let currentSurfaceIndex = this.product.currentSurfaceIndex;
                                    for( const [index, surface] of this.product.surfaces.entries() ) {
                                        if( changedSurfaces.includes(surface?.name) ) {
                                            await this.product.switchTo(this.product.surfaces[index]);
                                            response = await this.callEditorMethod("applyProductTheme", optionName);
                                        }
                                    }
                                    await this.product.switchTo(this.product.surfaces[currentSurfaceIndex]);
                                }
                                else {
                                    response = await this.callEditorMethod("applyProductTheme", optionName);
                                }
                                isDevXd() ? console.log(`applyProductTheme attempt ${x}`, response) : null;
                                if( response === true ) break;
                            }catch(e){
                                isDevXd() ? console.log('applyProductTheme failed for ', optionName, e): null;
                            }
                        }
                    }));

                    if(Object.keys(data).length !== 0){
                        isDevXd() ? console.log("loadUserInfo"): null;
                        await this.callEditorMethod("loadUserInfo", data);
                    } else {
                        isDevXd() ? console.log('nothing to bind'): null;
                    }

                    let saved = await this.saveProductState({ccConfig});
                    this.stateId = saved.stateId;
                    this.inputData = {
                        ...await setInputData(this.product, {}),

                        qrValues: userInfo['qrValues'],
                        styles: styleDefaults,
                    };
                }
            }
            isDevXd() ? console.log("inputData ", this.inputData): null;
            isDevXd() ? console.log("TIME_FINISH ", Math.floor(Date.now() / 1000)): null;

            //set text pickpackOptions from variations data.
            let surfaceIdMap = {};
            for( const config of ccConfig.ccConfigIds ) {
                surfaceIdMap[config.configId] = config.surfaceName;
            }
            if( this.props.templateVariations && !this.props.templateVariations?.error && this.props.templateVariations?.fields ) {
                for( const field of this.props.templateVariations.fields ) {
                    if( field?.type == 'proofing' && field.proofingField ) {
                        let value = getNestedValue(this.inputData, ["text", surfaceIdMap[field.proofingField.surfaceId], field.proofingField.fieldName]);
                        if( value !== null ) {
                            this.pickpackOptions = setNestedValue(this.pickpackOptions, ['text', surfaceIdMap[field.proofingField.surfaceId], field.proofingField.fieldName], value);
                        }
                    }
                }
            }
            let qrCodeLineItemOptionsExists = userData?.sourceData?.xd?.templateOptions?.options?.find(x => x.dev_key === "qr_code");
            if( qrCodeLineItemOptionsExists ) {
                this.inputData = setNestedValue( this.inputData, ['qrValues', 'tracking', 'redirectType'], this.props.qrCodeLineItemValue );
                this.inputData = setNestedValue( this.inputData, ['qrValues', 'tracking', 'redirectUrl'], this.props.qrCodeUrlValue );
            }

            this.oldInputData = this.inputData;

            this.setState({
                currentSurfaceIndex: this.product.currentSurfaceIndex,
                initialized: true,
                surfaceIdMap,
                pickpackStyleSelections
            });

            this.editor.eval(
                "let jq = document.createElement('script');" +
                "jq.src = 'https://unpkg.com/imask';" +
                "document.getElementsByTagName('head')[0].appendChild(jq)"
            );
            // this.setWarnings();

            // add handlers to capture select image button presses within the designer - apply once upon initialization
            // completion and again with each surface change event
            this.editor.eval(addEditorSelectImageButtonHandlers(this.ccVersionMajor));
            IFrameAPIEvents.subscribeToSurfaceChanged(this.editor, {
                onSurfaceChanged: async () => {
                    this.editor.eval(addEditorSelectImageButtonHandlers(this.ccVersionMajor));
                }
            });
            IFrameAPIEvents.subscribeToItemsChanged(this.editor, {
                onItemChanged: async (item) => {
                    if( item?.type == 'PlaceholderItem' && !item?._content ) {
                        this.editor.eval(addEditorSelectImageButtonHandlers(this.ccVersionMajor));
                    }
                }
            });

            IFrameAPIEvents.subscribeToItemsAdded(this.editor, {
                onItemAdded: async (item) => {
                    if(this.ccVersionMajor >= 7 && item.type == "BoundedTextItem" && item.textPermissions?.allowTextFormatting != true ){
                        item.textPermissions.allowTextFormatting = false;
                        await this.product.setItem(item);
                    }
                }
            });

            // add function for searching the iframe object model - only used during development
            if (isDevXd()) searchIframeObjectModel(this.editor);

        }
        catch (e) {
            // if (stateBroken === true) {
            //     this.setState({
            //         proofImages: ["assets/SPINNER_ERROR_STATE_FAILED.svg"],
            //         currentSurfaceIndex: 0,
            //         initialized: false,
            //     });
            // } else {
            // console.log("e", e);
                this.setState({
                    initializeError: e,
                    // proofImages: ["assets/SPINNER_ERROR.svg"],
                    currentSurfaceIndex: 0,
                    initialized: false,
                });
            // }
        }

    };

    // customData is not mutated
    // userInfo is not mutated
    setCustomFields = async (customFieldsData, userInfo, product) => {
        try {
            if (customFieldsData) {
                const CustomersCanvas = window["CustomersCanvas"] || null;
                let model =  await CustomersCanvas.DesignAtoms.ObjectModel;
                const indexOfCurrentSurface = product.surfaces.lastIndexOf(product.currentSurface);

                for( let surfaceIndex = 0; surfaceIndex < product.surfaces.length; surfaceIndex++ ){
                    let currentSurfaceCustomData = customFieldsData[product.surfaces[surfaceIndex].name];
                    if( currentSurfaceCustomData?.length){
                        const bindingResultData = userInfo?.['surfaces']?.[product.surfaces[surfaceIndex].name];
                        await product.switchTo(product.surfaces[surfaceIndex]);
                        const product2 = await this.callEditorMethod("getProduct");
                        const currentSurface = await product2.currentSurface;
                        for(const customField of currentSurfaceCustomData) {
                            let placeholder = new model.BoundedTextItem( "", new model.RectangleF(0,0,20,20), "Roboto-Regular", 10 );
                            placeholder.text = bindingResultData?.[customField.name] ?? "";
                            placeholder.name = customField.name;
                            placeholder.overflowStrategy = "fitToBox";
                            placeholder.visualizationPermissions.noPrint = true;
                            placeholder.visualizationPermissions.noShow = true;
                            placeholder.isVariable = true;
                            placeholder.isTextPlaceholder = true;
                            placeholder.textPermissions.allowChangeText = false;
                            placeholder.textRectangle.width = 200;
                            placeholder.textRectangle.height = 200;
                            placeholder.richTextMode = false;
                            await currentSurface.insertItem(placeholder);
                        }
                    }
                }
                await product.switchTo(product.surfaces[indexOfCurrentSurface]);
            }
        }
        catch (e) {
            console.log("Error with customFields: " , e);
        }
    };

    loadCanvas = async ({ccConfig, editorConfigurations, userInfo=null, assetSources, stateId = null, userData}) => {

        const iframe = this.editorDom;

        await loadScript({src: this.props.ccApis.scriptUrl, id: "CcIframeApiScript"});

        const CustomersCanvas = window["CustomersCanvas"] || null;

        this.ccVersion = CustomersCanvas.VERSION;
        this.ccVersionMajor = +this.ccVersion?.split(".")[0] || 0;
        if (stateId && this.props.ccHostResponse.value?.ccVersion && !this.ccVersion.startsWith(this.props.ccHostResponse.value.ccVersion)) {
            console.log(`State version changed from ${this.props.ccHostResponse.value?.ccVersion} to ${this.ccVersion}, removing state ${stateId} from order`);
            stateId = null;
        }

        const buildConfig = () => ({
            ...editorConfigurations,
            assetSources,
            userInfo,
            violationWarningsSettings: ccConfig.violationWarningsSettings ?? {},
            rendering: ccConfig.rendering ?? {},
        })

        // load the editor for an existing stateId if available
        if (stateId) {
            this.editor = await CustomersCanvas.IframeApi.loadEditor(iframe, stateId, buildConfig());
            this.product = await this.callEditorMethod("getProduct");
        } else {
            // initialize a new editor and stateId if no existing stateId
            this.editor = await CustomersCanvas.IframeApi.loadEditor(iframe, ccConfig.productDefinition, buildConfig());
            this.product = await this.callEditorMethod("getProduct");
            addImageReferencesToProductTagsFromInitializationData(this.product, userInfo, userData.sourceData.xd);
        }


        //For debugging purposes
        //@ts-ignore
        window.editor = this.editor;

        IFrameAPIEvents.subscribeToPopupOpen(this.editor, {
            //This is to add on change events to the text popup so it can be automatically formatted.
            onPopupOpen: async () => {

                //const selector = '#mainContainer > div.textPopup.popover.ui-draggable.ui-draggable-handle.textPopup--editor > div.popover-content > div > textarea';
                const selector = 'textarea';
                //remove existing events
                this.editor.eval(
                    "$('" + selector + "')" +
                    ".trigger('removeChangeEvent')"
                );
                //find the correct mask for this field
                const selectedItems = await this.callEditorMethod("getSelectedItems");
                const product = await this.callEditorMethod("getProduct");
                const surface = ccConfig.templateConfigurations[product.surfaces[this.state.currentSurfaceIndex].name];
                let inputMask;
                if(surface !== undefined && selectedItems[0] !== undefined && surface[selectedItems[0].name] !== undefined) {
                    inputMask = surface[selectedItems[0].name].inputMask;
                }
                //apply the event to the field
                if(inputMask !== undefined && inputMask !== '') {
                    //define functions to format the field input (inside the editor)
                    this.editor.eval(
                        appendUpdateFieldEvent(inputMask, selector)
                    );
                }
            }
        });

        resizeGripHandles(this.editor, 20);
        applyEditorStyleChanges(this.editor);

        // hide select image buttons when in advanced mode
        if (this.props.useAdvancedEditingMode) hideSelectImageButtons(this.editor);

        // // don't allow the editor to display the default asset manager if using the custom gallery
        // if (this.stateId.usesCustomGallery) hideDefaultAssetManager(this.editor);
    };

    render() {
        const {next} = this.props;
        const {currentSurfaceIndex, fullScreen, initialized, initializeError, surfaceIdMap, pickpackStyleSelections} = this.state;
        return cs(
            () => next({
                iframeRef: (r) => this.editorDom = r,
                initialize: this.initialize,
                initializeError,
                ccStateId: this.stateId,
                ccVersion: this.ccVersion,
                ccVersionMajor: this.ccVersionMajor,
                ccVersionMinor: +this.ccVersion?.split(".")[1] || 0,
                currentSide: currentSurfaceIndex,
                editor: this.editor,
                fullScreen,
                initialized,
                inputData: this.oldInputData ?? this.inputData, // to be saved to item.metaData.inputData, ignore pending changes.
                pickpackOptions: this.pickpackOptions,
                pickpackStyleSelections,

                override: this.override, // to be saved to event.overrideData
                // userData.overrideData

                surfaces: this.product?.surfaces,
                getSurfaces: () => this.product?.surfaces,

                changeSide: async (surfaceIndex, editing) => {
                    if (editing) {
                        await this.product.switchTo(this.product.surfaces[surfaceIndex]);
                    }
                    this.setState({currentSurfaceIndex: surfaceIndex});
                },


                // ---- START --- Refactor #4
                saveProductState: this.saveProductState,
                getAllItems: async () => {
                    const productModel = await this.product.getProductModel();
                    return await productModel.getAllItems();
                },
                subscribe: (eventType, action) => {

                    this.editor.subscribe(
                        eventType,
                        action
                    );
                },
                removeEditorSelectImageButtonPositionData: () => {
                    // remove select image button position information from the iframe so that it's not available for use
                    // when using the white select image button that accompanies an existing image selection
                    removeEditorSelectImageButtonPositionData(this.editor);
                },
                clearSelection: () => {
                    this.callEditorMethod("clearSelection");
                },
                getProduct: async () => {
                    return await this.callEditorMethod("getProduct");
                },

                hideDefaultAssetManager: () => {
                    this.editor.eval("document.head.insertAdjacentHTML('afterbegin','<style>.manager-wrapper {display:none !important;}</style>')");
                },
                editorEval: (script) => this.editor.eval(script),
                callEditorMethod: (name, ...params) => this.callEditorMethod(name, ...params),
                // getEditorContext: () => this.editor._context,
                getEditor: () => this.editor,
                addEditorSelectImageButtonHandlers: () => this.editor.eval(addEditorSelectImageButtonHandlers(this.ccVersionMajor)),
                // ---- END --- Refactor #4

                saveProduct: async ({ccConfig}) => {
                    const finishedProduct = await this.callEditorMethod("finishProductDesign", {stateId: this.stateId});
                    if (finishedProduct) {
                        this.override = await setOverrideData(
                            finishedProduct.userChanges.surfaces,
                            this.product, ccConfig.templateBindingData,
                            this.override
                        );
                    }
                    this.inputData = await setInputData(this.product, this.inputData);
                    if( this.props.templateVariations && !this.props.templateVariations?.error && this.props.templateVariations?.fields ) {
                        for( const field of this.props.templateVariations.fields ) {
                            if( field?.type == 'proofing' && field.proofingField ) {
                                let value = getNestedValue(this.inputData, ["text", surfaceIdMap[field.proofingField.surfaceId], field.proofingField.fieldName]);
                                if( value !== null ) {
                                    this.pickpackOptions = setNestedValue(this.pickpackOptions, ['text', surfaceIdMap[field.proofingField.surfaceId], field.proofingField.fieldName], value);
                                }
                            }
                        }
                    }

                    // add variable data merge fields to input data - will be formatted as:
                    // {"Surface Name": {"Variable Tag Name": {type: "", defaultValue: "", mailingListField: ""}}}
                    if (this.allowsVariableData) {

                        const mergeFields = await variableDataHandler.getUsedMergeFields(
                            await this.props.apis.next.templateAdmin.getVariableDataMergeFields(),
                            this
                        );
                        let variableData = {};

                        for (const mergeField of mergeFields) {

                            // create the surface key if not created in a previous iteration
                            if (!Object.keys(variableData).includes(mergeField.surfaceName))
                                variableData[mergeField.surfaceName] = {};

                            // if the tag name is already present for the surface, then skip
                            if (Object.keys(variableData[mergeField.surfaceName]).includes(mergeField.tagName))
                                continue;

                            // add tag name to surface with merge field details
                            variableData[mergeField.surfaceName][mergeField.tagName] = {
                                type: mergeField.type,
                                defaultValue: mergeField.defaultValue,
                                dataSource: mergeField.dataSource
                            }

                        }

                        this.inputData.variableData = variableData;

                    }

                    this.oldInputData = this.inputData;
                    // this.setState({isRequired: await this.checkIsRequired()})
                    return await this.saveProductState({ccConfig});
                },

                saveProductModel: async () => {
                    if (!this.product) {
                        return;
                    }
                    let productModel = await this.product.getProductModel();

                    this.setState({
                        productModel: productModel
                    });
                },

                loadProductModel: async () => {
                    try{
                        this.inputData = this.oldInputData;
                        await this.product.setProductModel(this.state.productModel);
                        await this.product.switchTo(this.product.surfaces[currentSurfaceIndex]);
                        // this.setState({isRequired: await this.checkIsRequired()});
                    }
                    catch(e){
                        console.log("Error loading product modal " , e);
                    }
                },

                applyQrImage: async (qrField, imageUrl) => {
                    let payload = {surfaces: {}};
                    payload.surfaces[qrField.side] = {};
                    payload.surfaces[qrField.side][qrField.id] = imageUrl;
                    if (isDevXd()) console.log("CcEditor.applyQrImage editor.loadUserInfo payload", payload);
                    if( imageUrl ) {
                        await this.callEditorMethod("loadUserInfo", payload);
                    }
                    else {
                        let productModel = await this.product.getProductModel();
                        let items = await productModel.getAllItems();
                        let item = items.find( (item) => {
                            return (
                                item.name === qrField.id &&
                                item.content &&
                                qrField.side === item.parentContainer.parentComponent.name
                            );
                        });
                        if (item) {
                            item.content = null;
                            await this.product.setItem(item);
                            //Set item doesn't visually clear the image on its own. Need another interaction to clear it visually from the editor.
                            await this.editor.selectAllItems();
                            await this.editor.selectItems([]);
                        }
                    }
                },

                getQrFields: ({ccConfig, qrCodeLineItem}) => {
                    let qrFields = [];
                    Object.entries(ccConfig?.templateBindingData ?? {}).filter(([surface, fields])=>{
                        Object.entries(fields).filter(([field, value])=>{
                            if (value?.['type'] === "qrCode") {
                                qrFields.push({
                                    'id': field,
                                    'isRequired': getNestedValue(ccConfig.templateConfigurations, [surface, field, "isRequired"]) || false,
                                    'qrType': value?.['qrType'] ?? [],
                                    'qrPrefs': value?.['qrPrefs'],
                                    'side': surface,
                                    'lineItemValue': this.props.qrCodeLineItemValue ?? ''
                                });
                            }
                            else if (value?.['type'] === "qrCodeLineItem" && qrCodeLineItem?.subOptions?.length) {
                                let qrTypes = [];
                                qrCodeLineItem.subOptions.forEach(option => {
                                    qrTypes.push(option.dev_key);
                                });
                                qrFields.push({
                                    'id': field,
                                    'isRequired': qrCodeLineItem.isRequired || getNestedValue(ccConfig.templateConfigurations, [surface, field, "isRequired"]),
                                    'qrType': qrTypes,
                                    'qrPrefs': null,
                                    'side': surface,
                                    'lineItemValue': this.props.qrCodeLineItemValue ?? ''
                                });
                            }
                        });
                    });
                    return qrFields;
                },

                setQrValue: ( currentSide, fieldName, valueName, text ) => {
                    this.inputData = setNestedValue(this.inputData, ['qrValues', currentSide, fieldName, valueName], text);
                },

                setTrackingValue: ( trackingObj ) => {
                    this.inputData = setNestedValue(this.inputData, ['qrValues', 'tracking'], trackingObj);
                },

                setStyle: async ( currentSide, style, styleName, optionName ) =>{
                    if (style) {
                        let productModel = await this.product.getProductModel();
                        let items = await productModel.getAllItems();
                        let changedSurfaces = [];
                        let skipStyle = false;

                        if( this.ccVersionMajor > 7 || ( this.ccVersionMajor == 7 && (
                            +this.ccVersion?.split(".")[1] > 1 || +this.ccVersion?.split(".")[2] >= 27
                        ) ) ) {
                            /*Skip setting style manually unless style applies to another surface or multi-tagged. Just
                            * rely on applyTheme from CustCanv.
                            */
                            skipStyle = true;
                            let styleKeys = Object["keys"](style);
                            items.forEach((item, index)=>{
                                if( item.parentContainer?.parentComponent?.name != this.product.surfaces[currentSurfaceIndex].name ) {
                                    skipStyle = false;
                                }
                                else if(item.themeBinding?.styles?.length > 1) {
                                    let styleRelatedTags = item.themeBinding.styles.filter(e => styleKeys.indexOf(e) !== -1);
                                    if( styleRelatedTags.length > 1 ) skipStyle = false;
                                }
                            });
                        }
                        if( !skipStyle ) {
                            let multiTagged = false;
                            items.forEach((item, index)=>{
                                let firstTrue = false
                                /* V7.1.27 sets visible flag based on last tag. Need to prevent setting over it when dealing with multi tags.
                                * Handle visibility ourselves. Mixing multi tags fields or applying styles to surfaces not displayed leads
                                * to all kinds of issues with visibility. Easier to just handle on our end.
                                */
                                Object["entries"](style).forEach(([tag,values])=>{
                                    if( values["visible"] !== undefined && item.themeBinding?.styles?.includes(tag) ){
                                        if(!firstTrue && items[index].visible != values["visible"]){
                                            items[index].visible = values["visible"] === 'true';
                                            if( !changedSurfaces.includes(item?.parentContainer?.parentComponent?.name) ) {
                                                changedSurfaces.push(item?.parentContainer?.parentComponent?.name);
                                            }
                                            if( item.themeBinding?.styles?.length > 1 ) multiTagged = true;
                                        }
                                        if(!firstTrue){
                                            firstTrue = values["visible"] === 'true';
                                        }
                                    }
                                });
                            });
                            if( changedSurfaces.length ) {
                                try{
                                    await this.product.setProductModel(productModel);
                                    //version 7 text wrap isn't updating without applyProductTheme or setItem while the items surface is loaded.
                                    //Can't applyProductTheme after setItem for greater than V7.1.27 as that sets visibility and breaks multi tags set.
                                    if( this.ccVersionMajor >=7 && (
                                        ( +this.ccVersion?.split(".")[1] <= 1 && +this.ccVersion?.split(".")[2] < 27 )
                                        || !multiTagged
                                    ) ) {
                                        for( const [index, surface] of this.product.surfaces.entries() ) {
                                            if( changedSurfaces.includes(surface?.name) ) {
                                                await this.product.switchTo(this.product.surfaces[index]);
                                                await this.callEditorMethod("applyProductTheme", optionName);
                                            }
                                        }
                                    }
                                    await this.product.switchTo(this.product.surfaces[currentSurfaceIndex]);
                                    this.editor.eval(addEditorSelectImageButtonHandlers(this.ccVersionMajor));
                                }
                                catch(e){
                                    console.log("Error initializing the styles " , e);
                                }
                            }
                        }
                        this.inputData = setNestedValue(this.inputData, ['styles', currentSide, styleName], optionName);
                    }
                },
                // Change a theme.
                applyTheme : async (themeName, isColorSet = false) =>{
                    await this.callEditorMethod("applyProductTheme", themeName);
                    if (isColorSet) {
                        this.inputData = setNestedValue(this.inputData, ['colorSet'], themeName);
                    }
                },
                openFullScreenMode: () => {
                    this.setState({fullScreen: true});
                },
                closeFullScreenMode: () => {
                    this.setState({fullScreen: false});
                },

            }),
        );
    }
}


const applyEditorStyleChanges = (editor) => {

    let styles = [];

    // remove the "done" button
    styles.push("div button[title~=Done] { display:none; }");

    // remove bottom padding CC6 adds at small width
    styles.push("@media (max-width: 480px) { .aur-theme #mainRow #canvasWrapper.canvas-object-inspector { padding-bottom: 0 !important; } }");

    editor.eval("document.head.insertAdjacentHTML('afterbegin','<style>" + styles.join(" ") + "</style>')");

};

const hideSelectImageButtons = (editor) => {

    editor.eval("document.head.insertAdjacentHTML('afterbegin','<style>.cc-icon-select-image {display:none !important;}</style>')");

}

// removes existing select image button position information - this should be called when making any selection within
// the editor so that any previous select image button clicks aren't used for determining the designer image to edit
const removeEditorSelectImageButtonPositionData = (editor) => {

    editor.eval([
        "if (window.xdData && window.xdData.LastSelectImageButtonPosition) {", // process only if the xdData variable exists
        "   delete window.xdData.LastSelectImageButtonPosition;", // delete button position information
        isDevXd() && "console.log('FROM IFRAME: Removed select image button position information');",
        "}",
    ].join(""));

}

const resizeGripHandles = (editor, size) => {

    // this works with CC5 only - there may be an equivalent method in CC6, but leaving as default until a change is
    // requested
    editor.eval([
        "if (spEditor.productHandler.product.version.startsWith('5')) {",
        "   spEditor.productHandler._canvas._resizeGripSize = " + size + ";",
        "   spEditor._canvas._updateGripsAndHoverDiv();",
        "}"
    ].join(""));

};
